/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from 'react';
import {
  Alert, Snackbar, Container, Divider, Button, LinearProgress, Box, TextField, IconButton,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { ChevronRightRounded } from '@mui/icons-material';
import { getRequestedItemDetails, updateRequestedItemStatus } from '../../../../Redux/Models/market';
import Elements from '../../../Elements';
import ROUTES from '../../../../Configs/routes';
import AlertContext from '../../../../Context/AlertContext';
import ModalBase from '../../../Elements/ModalBase';
import Market from '../Market';
import ConfirmationContext from '../../../../Context/ConfirmationContext';
import { formatNumToCurrency } from '../../../../Utils/helpers';
import Images from '../../../../Configs/images';

export default function RequestedItemDetails({
  classes, error, message, requestedItemDetails, isLoading, submitted,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { requestId } = useParams();
  const { openAlert } = useContext(AlertContext);
  const { openConfirmation, closeConfirmation } = useContext(ConfirmationContext);
  const { status } = requestedItemDetails;
  const inSearching = status === 'In searching';
  const rejected = status === 'Rejected';
  const available = status === 'Available';
  const open = status === 'Open';

  const [selectedMarketItem, setItemFromMarket] = useState({});
  const [openMarket, setOpenMarket] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const updateStatus = () => {
    const body = {
      status: newStatus,
      item_id: selectedMarketItem.id,
    };
    dispatch(updateRequestedItemStatus(requestId, body));
  };

  const openAcceptPop = () => {
    if (newStatus) {
      openConfirmation({
        handleAgree: () => updateStatus(),
        handleClose: () => closeConfirmation(),
        desc: 'Make sure information is correct',
        title: 'Confirm Send Information?',
        confirmText: 'Yes, Confirm',
        direction: 'row-reverse',
        minWidth: 500,
        maxWidth: 'md',
        btnTextSize: '14px',
      });
    } else if ((newStatus === 'Found' && !selectedMarketItem.id) || !newStatus) {
      openAlert({
        open: true,
        message: 'Please complete information results first',
        severity: 'warning',
      });
    }
  };

  useEffect(() => {
    if (error) {
      openAlert({
        open: true,
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  useEffect(() => {
    dispatch(getRequestedItemDetails(requestId));
  }, []);

  const handleSaveItem = (item) => {
    setItemFromMarket(item);
    setOpenMarket(false);
  };

  useEffect(() => {
    if (submitted && rejected) {
      openAlert({
        open: true,
        message: 'You have rejected this requested item',
        severity: 'info',
      });
      closeConfirmation();
    }
    if (submitted && inSearching) {
      openAlert({
        open: true,
        message: 'Item in searching',
        severity: 'success',
      });
      closeConfirmation();
    }
    if (submitted && available) {
      openAlert({
        open: true,
        message: 'We found the item. Notification has sent to user',
        severity: 'success',
      });
      closeConfirmation();
    }
  }, [requestedItemDetails, submitted]);

  return (
    <Elements.AdminPageBase
      navigate={navigate}
      title="Market"
    >

      {isLoading && <LinearProgress color="inherit" sx={{ mt: 2 }} />}

      <div className={classes.root}>
        <Container maxWidth="md">
          <div className="info-card mt-3">
            <h4>User information</h4>
            <Divider sx={{ my: 2 }} />
            <div className="flex space-between">
              <div className="flex-1">
                <div className="s-12 mute">Request ID</div>
                <div className="weight-700 mt-1">{requestedItemDetails.id}</div>
              </div>
              <div className="flex-1">
                <div className="s-12 mute">Request date</div>
                <div className="weight-700 mt-1">{moment(new Date(requestedItemDetails.created_at)).format('MMM, DD yyyy')}</div>
              </div>
            </div>
            <div className="flex space-between mt-3">
              <div className="flex-1">
                <div className="s-12 mute">Customer name</div>
                <div className="weight-700 mt-1">{requestedItemDetails.pickup_address?.name}</div>
              </div>
              <div className="flex-1">
                <div className="s-12 mute">Request status</div>
                <div className="weight-500 mt-1">
                  <div className="pil inline-block s-14">{status}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="info-card mt-3">
            <h4>Request information</h4>
            <Divider sx={{ my: 2 }} />
            <div>
              <div className="s-12 mute">Item name</div>
              <div className="weight-700 mt-1">
                {requestedItemDetails.name}
              </div>
            </div>
            <div className="mt-3">
              <div className="s-12 mute">Item category</div>
              <div className="weight-700 mt-1">
                {requestedItemDetails.category_name}
              </div>
            </div>
            <div className="mt-3">
              <div className="s-12 mute">Notes</div>
              <div className="weight-700 mt-1">
                {requestedItemDetails.notes}
              </div>
            </div>
            <div className="mt-3">
              <div className="s-12 mute">Photo</div>
              <img
                width="120"
                height="120"
                alt="requested item"
                src={requestedItemDetails.photo}
                className="rounded link cover mt-1"
              />
            </div>
          </div>

          <div className="info-card mt-3">
            <h4>Information results</h4>
            <Divider sx={{ my: 2 }} />

            <Box mt={2} display={available ? 'none' : 'block'}>
              <div className="s-12 mute">Results</div>
              <Box display="flex" gap={0.5} mt={0.5}>
                <Button onClick={() => setNewStatus(newStatus !== 'Found' ? 'Found' : '')} fullWidth className={newStatus === 'Found' ? 'btn-contained' : 'btn-outlined'} variant={newStatus === 'Found' ? 'contained' : 'outlined'}>Found</Button>
                <Button onClick={() => setNewStatus(newStatus !== 'Not Found' ? 'Not Found' : '')} fullWidth className={newStatus === 'Not Found' ? 'btn-contained' : 'btn-outlined'} variant={newStatus === 'Not Found' ? 'contained' : 'outlined'}>Not Found</Button>
              </Box>
            </Box>

            <Box mt={2} display={newStatus === 'Found' ? 'block' : 'none'}>
              <div className="s-12 mute">Choose from market</div>
              {selectedMarketItem.id
                ? (
                  <Box display="flex" gap={1.5} mt={0.5} border="1px solid #E5E5E5" borderRadius="5px" p={2} onClick={() => setOpenMarket(true)}>
                    <img
                      width="80px"
                      height="80px"
                      className="rounded cover"
                      src={selectedMarketItem.photo}
                      alt="baco"
                    />
                    <Box flex={1}>
                      <Box className="weight-600">{selectedMarketItem.name}</Box>
                      <Box display="flex" className="s-10" color="#586479" gap={0.5} flexWrap="wrap" alignItems="center" mt={0.25}>
                        <div>{selectedMarketItem.category_name || '-'}</div>
                        <div>
                          ・
                          {selectedMarketItem.condition_name || '-'}
                        </div>
                        <div>
                          ・
                          {selectedMarketItem.volume || '0 mL'}
                        </div>
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                        <Box className="s-16 weight-700">
                          {formatNumToCurrency(Number(selectedMarketItem.price))}
                        </Box>
                        <Box display="flex" gap={0.5}>
                          <Box fontSize={12}>Qty 1</Box>
                          <img src={Images.CheckRed} alt="check-icon" />
                        </Box>
                      </Box>
                      {selectedMarketItem.sale_type === 'Bid Price'
                      && (
                      <Box className="s-12" mt={0.5}>
                        {selectedMarketItem.bidder_count}
                        {' '}
                        bids・
                        {moment(selectedMarketItem.end_date).format('DD MMM YYYY (HH.mm)')}
                      </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <TextField
                    sx={{ mt: 0.5 }}
                    fullWidth
                    size="small"
                    value=""
                    className="link"
                    placeholder="Choose from market"
                    onClick={() => setOpenMarket(true)}
                    InputProps={{
                      endAdornment: (
                        <IconButton sx={{ p: 0 }}>
                          <ChevronRightRounded />
                        </IconButton>
                      ),
                    }}
                  />
                )}
            </Box>
          </div>

          <div className="flex space-between gap-16 mt-5">
            <Button sx={{ px: 8 }} variant="outlined" className="action btn-outlined" onClick={() => navigate(-1)}>Back</Button>
            <Button disabled={available} sx={{ px: 4 }} variant="contained" className="action btn-contained" onClick={openAcceptPop}>Send Information</Button>
          </div>
        </Container>
      </div>

      <ModalBase open={openMarket} fullWidth maxWidth="lg" noAgree noCancel handleClose={() => setOpenMarket(false)}>
        <Market toSelect={handleSaveItem} />
      </ModalBase>
    </Elements.AdminPageBase>
  );
}
